import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { clientFormAPI } from '../services/api';
import { 
  User, Home, Wallet, CreditCard, Send, Check, AlertCircle, 
  Edit3, ChevronDown, ChevronUp, Loader2, CheckCircle, Info, Leaf
} from 'lucide-react';

const ClientForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    identite: true,
    situation: false,
    ressources: false,
    charges: false
  });

  // Form data
  const [formData, setFormData] = useState({
    identite: {},
    situation: {},
    ressources: {},
    charges: {},
    message_client: ''
  });

  // Champs obligatoires si vides
  const requiredFields = {
    identite: ['nom', 'prenom', 'adresse', 'telephone'],
    situation: [],
    ressources: [],
    charges: []
  };

  useEffect(() => {
    loadDossier();
  }, [token]);

  const loadDossier = async () => {
    try {
      setLoading(true);
      const res = await clientFormAPI.getDossierByToken(token);
      setDossier(res.data);
      
      // Pré-remplir le formulaire
      setFormData({
        identite: res.data.identite || {},
        situation: res.data.situation || {},
        ressources: res.data.ressources || {},
        charges: res.data.charges || {},
        message_client: ''
      });
      
      // Vérifier si déjà soumis
      if (res.data.client_submitted) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Erreur chargement:', err);
      setError('Ce lien est invalide ou a expiré.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isFieldEmpty = (section, field) => {
    const value = formData[section]?.[field];
    return value === null || value === undefined || value === '' || value === 0;
  };

  const isFieldRequired = (section, field) => {
    return requiredFields[section]?.includes(field) && isFieldEmpty(section, field);
  };

  const getMissingRequiredFields = () => {
    const missing = [];
    Object.entries(requiredFields).forEach(([section, fields]) => {
      fields.forEach(field => {
        if (isFieldEmpty(section, field)) {
          missing.push({ section, field });
        }
      });
    });
    return missing;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier les champs obligatoires
    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      alert('Veuillez remplir tous les champs obligatoires marqués d\'un *');
      return;
    }

    setSubmitting(true);
    try {
      await clientFormAPI.submitForm(token, formData);
      setSubmitted(true);
    } catch (err) {
      console.error('Erreur soumission:', err);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (section, field, label, type = 'text', options = null) => {
    const value = formData[section]?.[field] ?? '';
    const isEmpty = isFieldEmpty(section, field);
    const isRequired = requiredFields[section]?.includes(field);
    const originalValue = dossier?.[section]?.[field];
    const wasEmpty = originalValue === null || originalValue === undefined || originalValue === '' || originalValue === 0;

    return (
      <div className={`${isEmpty && isRequired ? 'bg-amber-50 border-l-4 border-amber-400 pl-3 py-2 rounded-r' : ''}`}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {isRequired && isEmpty && <span className="text-red-600 ml-1">*</span>}
          {!wasEmpty && !isEmpty && (
            <span className="ml-2 text-xs text-green-600 inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Déjà renseigné
            </span>
          )}
        </label>
        {type === 'select' && options ? (
          <select
            value={value}
            onChange={(e) => handleChange(section, field, e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${
              isEmpty && isRequired ? 'border-amber-400 bg-white' : 'border-gray-300'
            }`}
          >
            <option value="">— Sélectionner —</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            value={value}
            onChange={(e) => handleChange(section, field, e.target.value)}
            rows="3"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${
              isEmpty && isRequired ? 'border-amber-400 bg-white' : 'border-gray-300'
            }`}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={(e) => handleChange(section, field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${
              isEmpty && isRequired ? 'border-amber-400 bg-white' : 'border-gray-300'
            }`}
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Merci !</h1>
          <p className="text-gray-600 mb-4">
            Vos informations ont été envoyées avec succès.
          </p>
          <p className="text-sm text-gray-500">
            Référence dossier : <strong>{dossier?.reference}</strong>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Votre gestionnaire va traiter votre demande dans les meilleurs délais.
          </p>
        </div>
      </div>
    );
  }

  const missingCount = getMissingRequiredFields().length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-gray-900 flex items-center justify-center mb-4">
            Soc<span className="relative inline-block">
              <span className="invisible">i</span>
              <span className="absolute bottom-0 left-0">ı</span>
              <Leaf className="absolute -top-2.5 left-0 w-6 h-6 text-emerald-500 fill-emerald-500" />
            </span>alAC
          </h1>
          <p className="text-gray-600">Compléter votre dossier</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
            <span className="text-sm text-gray-600">Référence :</span>
            <span className="font-semibold text-green-600">{dossier?.reference}</span>
          </div>
        </div>

        {/* Alerte champs manquants */}
        {missingCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">
                {missingCount} information{missingCount > 1 ? 's' : ''} obligatoire{missingCount > 1 ? 's' : ''} à compléter
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Les champs marqués d'un <span className="text-red-600">*</span> sont obligatoires et doivent être remplis.
              </p>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">
            Les informations déjà renseignées sont pré-remplies. Vous pouvez les modifier si nécessaire.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Section Identité */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('identite')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 transition"
            >
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-gray-900">Identité</span>
                {getMissingRequiredFields().filter(f => f.section === 'identite').length > 0 && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                    À compléter
                  </span>
                )}
              </div>
              {expandedSections.identite ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            {expandedSections.identite && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField('identite', 'nom', 'Nom de naissance')}
                  {renderField('identite', 'prenom', 'Prénom')}
                  {renderField('identite', 'nom_usage', 'Nom d\'usage')}
                  {renderField('identite', 'date_naissance', 'Date de naissance', 'date')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('identite', 'nss', 'N° Sécurité sociale')}
                  {renderField('identite', 'cle_nss', 'Clé')}
                </div>
                {renderField('identite', 'adresse', 'Adresse')}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('identite', 'code_postal', 'Code postal')}
                  {renderField('identite', 'ville', 'Ville')}
                  {renderField('identite', 'pays', 'Pays')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('identite', 'telephone', 'Téléphone portable')}
                  {renderField('identite', 'telephone_fixe', 'Téléphone fixe')}
                  {renderField('identite', 'email', 'Email', 'email')}
                </div>
              </div>
            )}
          </div>

          {/* Section Situation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('situation')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition"
            >
              <div className="flex items-center gap-3">
                <Home className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-900">Situation</span>
              </div>
              {expandedSections.situation ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            {expandedSections.situation && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField('situation', 'type_logement', 'Type de logement', 'select', [
                    { value: 'Maison', label: '🏠 Maison' },
                    { value: 'Appartement', label: '🏢 Appartement' }
                  ])}
                  {renderField('situation', 'statut_occupation', 'Statut d\'occupation', 'select', [
                    { value: 'Propriétaire', label: 'Propriétaire' },
                    { value: 'Locataire', label: 'Locataire' },
                    { value: 'Hébergé', label: 'Hébergé' }
                  ])}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField('situation', 'situation_familiale', 'Situation familiale', 'select', [
                    { value: 'Célibataire', label: 'Célibataire' },
                    { value: 'Marié(e)', label: 'Marié(e)' },
                    { value: 'Pacsé(e)', label: 'Pacsé(e)' },
                    { value: 'Vie maritale', label: 'Vie maritale' },
                    { value: 'Divorcé(e)', label: 'Divorcé(e)' },
                    { value: 'Séparé(e)', label: 'Séparé(e)' },
                    { value: 'Veuf(ve)', label: 'Veuf(ve)' }
                  ])}
                  {renderField('situation', 'situation_pro_demandeur', 'Situation professionnelle', 'select', [
                    { value: 'Retraité(e)', label: 'Retraité(e)' },
                    { value: 'Salarié(e)', label: 'Salarié(e)' },
                    { value: "Demandeur d'emploi", label: "Demandeur d'emploi" },
                    { value: 'En arrêt maladie', label: 'En arrêt maladie' },
                    { value: 'En invalidité', label: 'En invalidité' },
                    { value: 'Autre', label: 'Autre' }
                  ])}
                </div>
              </div>
            )}
          </div>

          {/* Section Ressources */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('ressources')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100 transition"
            >
              <div className="flex items-center gap-3">
                <Wallet className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-gray-900">Ressources mensuelles</span>
              </div>
              {expandedSections.ressources ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            {expandedSections.ressources && (
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-500 mb-4">Indiquez vos revenus mensuels nets en euros (€)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('ressources', 'salaires', 'Salaires', 'number')}
                  {renderField('ressources', 'indemnites_chomage', 'Indemnités chômage', 'number')}
                  {renderField('ressources', 'pension_invalidite_ss', 'Pension invalidité', 'number')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('ressources', 'retraite_base', 'Retraite de base', 'number')}
                  {renderField('ressources', 'retraite_complementaire', 'Retraite complémentaire', 'number')}
                  {renderField('ressources', 'allocation_logement', 'Allocation logement (APL)', 'number')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('ressources', 'rsa_prime_activite', 'RSA / Prime activité', 'number')}
                  {renderField('ressources', 'aah_aeeh_pch', 'AAH / AEEH / PCH', 'number')}
                  {renderField('ressources', 'autres_revenus', 'Autres revenus', 'number')}
                </div>
                {renderField('ressources', 'autres_revenus_detail', 'Précisions autres revenus')}
              </div>
            )}
          </div>

          {/* Section Charges */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection('charges')}
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 transition"
            >
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-orange-600" />
                <span className="font-semibold text-gray-900">Charges mensuelles</span>
              </div>
              {expandedSections.charges ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            {expandedSections.charges && (
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-500 mb-4">Indiquez vos charges mensuelles en euros (€)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('charges', 'loyer', 'Loyer (avant APL)', 'number')}
                  {renderField('charges', 'energie', 'Énergie (élec, gaz)', 'number')}
                  {renderField('charges', 'eau', 'Eau', 'number')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('charges', 'assurance_habitation', 'Assurance habitation', 'number')}
                  {renderField('charges', 'assurance_vehicule', 'Assurance véhicule', 'number')}
                  {renderField('charges', 'cotisations_mutuelle', 'Mutuelle/Prévoyance', 'number')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderField('charges', 'telephonie', 'Téléphone/Internet', 'number')}
                  {renderField('charges', 'impot_revenu', 'Impôt sur le revenu', 'number')}
                  {renderField('charges', 'autres_charges', 'Autres charges', 'number')}
                </div>
                {renderField('charges', 'autres_charges_detail', 'Précisions autres charges')}
              </div>
            )}
          </div>

          {/* Message au gestionnaire */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message pour votre gestionnaire (optionnel)
            </label>
            <textarea
              value={formData.message_client}
              onChange={(e) => setFormData(prev => ({ ...prev, message_client: e.target.value }))}
              rows="3"
              placeholder="Informations complémentaires, questions..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Bouton Soumettre */}
          <button
            type="submit"
            disabled={submitting || missingCount > 0}
            className="w-full py-4 bg-green-600 text-white rounded-xl font-semibold text-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-3 shadow-lg"
          >
            {submitting ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-6 h-6" />
                Envoyer mes informations
              </>
            )}
          </button>

          {missingCount > 0 && (
            <p className="text-center text-amber-600 text-sm">
              Veuillez compléter les {missingCount} champ{missingCount > 1 ? 's' : ''} obligatoire{missingCount > 1 ? 's' : ''} avant d'envoyer.
            </p>
          )}
        </form>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p className="flex items-center justify-center gap-1">
            Soc<span className="relative inline-block">
              <span className="invisible">i</span>
              <span className="absolute bottom-0 left-0">ı</span>
              <Leaf className="absolute -top-0.5 left-0 w-2.5 h-2.5 text-emerald-500 fill-emerald-500" />
            </span>alAC
          </p>
          <p className="mt-1">Vos données sont protégées et traitées de manière confidentielle.</p>
        </div>
      </div>
    </div>
  );
};

export default ClientForm;
